import { css } from '@emotion/react'
import { Text, Theme, useTheme, VFlow } from 'bold-ui'
import { AlertaSuporteDecisaoClinica } from 'hooks/sdc/model-sdc'
import { useAlertasSdc } from 'hooks/sdc/useAlertasSdc'
import { useEffect, useMemo } from 'react'

import { SdcSecaoAccordion } from './SdcSecaoAccordion'
import { SdcSecaoAlerta } from './SdcSecaoAlerta'

export interface SdcDetalhesModalBodyProps {
  alertaSelecionado: AlertaSuporteDecisaoClinica
}

export const SdcDetalhesModalBody = (props: SdcDetalhesModalBodyProps) => {
  const { alertaSelecionado } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const { alertasSdc, marcarAlertaComoLido } = useAlertasSdc()

  const paddingConteudo = useMemo(() => !alertaSelecionado?.id.includes('RISCO_CARDIOVASCULAR'), [alertaSelecionado])

  useEffect(() => {
    const alerta = alertasSdc.find((alerta) => alerta.id === alertaSelecionado.id)
    alerta && marcarAlertaComoLido(alerta)
  }, [alertaSelecionado.id, alertasSdc, marcarAlertaComoLido])

  return (
    <VFlow vSpacing={0.75} style={styles.contentBody} data-testid='SdcDetalhesModalBody'>
      <VFlow vSpacing={0} style={styles.mainHeading}>
        <Text variant='h3' color='primary'>
          {alertaSelecionado.titulo}
        </Text>
        <Text variant='h3' fontWeight='normal' color='primary'>
          {alertaSelecionado.subtitulo !== alertaSelecionado.titulo && alertaSelecionado.subtitulo}
        </Text>
      </VFlow>
      <VFlow vSpacing={1.5}>
        {alertaSelecionado?.conteudoPersonalizado && (
          <SdcSecaoAlerta conteudo={alertaSelecionado.conteudoPersonalizado} paddingConteudo={paddingConteudo} />
        )}
        {alertaSelecionado?.exames && (
          <SdcSecaoAlerta
            titulo='Exames de apoio diagnóstico'
            conteudo={alertaSelecionado.exames}
            paddingConteudo={paddingConteudo}
          />
        )}
        {alertaSelecionado?.medicamentos && (
          <SdcSecaoAlerta
            titulo={alertaSelecionado.tituloMedicamentos ?? 'Medicamentos sugeridos'}
            conteudo={alertaSelecionado.medicamentos}
            paddingConteudo={paddingConteudo}
          />
        )}
        {alertaSelecionado?.recomendacoes && (
          <SdcSecaoAlerta
            titulo={!alertaSelecionado.id.includes('ALERTA_PRE_ECLAMPSIA') ? 'Recomendações gerais' : ''}
            conteudo={alertaSelecionado.recomendacoes}
            paddingConteudo={paddingConteudo}
          />
        )}
        {alertaSelecionado?.retorno && (
          <SdcSecaoAlerta
            titulo={alertaSelecionado.tituloRetorno ?? 'Retorno'}
            conteudo={alertaSelecionado.retorno}
            paddingConteudo={paddingConteudo}
          />
        )}
        {alertaSelecionado?.orientacoes && (
          <>
            <SdcSecaoAlerta
              titulo={alertaSelecionado?.tituloOrientacoes || 'Orientações ao cidadão'}
              conteudo={alertaSelecionado.orientacoes}
              paddingConteudo={paddingConteudo}
            />
          </>
        )}
        {alertaSelecionado?.comoFunciona && (
          <SdcSecaoAlerta
            titulo='Como funciona?'
            conteudo={alertaSelecionado.comoFunciona}
            paddingConteudo={paddingConteudo}
          />
        )}
        {alertaSelecionado?.conjuntoFatoresSdc && (
          <SdcSecaoAccordion conjuntoFatoresSdc={alertaSelecionado.conjuntoFatoresSdc} />
        )}
      </VFlow>
      {alertaSelecionado?.referencias && alertaSelecionado.referencias.length > 0 && (
        <VFlow vSpacing={1} style={styles.referencias}>
          <Text
            variant='h4'
            dangerouslySetInnerHTML={{ __html: alertaSelecionado.tituloReferencias ?? 'Referências' }}
          />
          {alertaSelecionado.referencias?.map((referencia, index) => (
            <Text
              key={index}
              dangerouslySetInnerHTML={{ __html: referencia }}
              variant='h5'
              fontWeight='normal'
              display='block'
            />
          ))}
        </VFlow>
      )}
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  contentBody: css`
    width: 32.25rem;
    padding: 1rem;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;

    ${theme.breakpoints.up('lg')} {
      width: 37.5rem;
    }

    ::-webkit-scrollbar {
      width: 0.25rem;
    }
    ::-webkit-scrollbar-track {
      background: ${theme.pallete.surface.main};
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 1rem;
      background-color: ${theme.pallete.gray.c70};
    }
  `,
  mainHeading: css`
    padding-bottom: 1rem;
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  referencias: css`
    padding-top: 1rem;
    margin-top: 0.25rem;
    border-top: 1px solid ${theme.pallete.divider};

    div:first-of-type {
      margin-bottom: 0;
    }
  `,
})
